import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SyncPreview } from '../components/sync/SyncPreview';
import { SyncTimeline } from '../components/sync/SyncTimeline';
import { SyncTransport } from '../components/sync/SyncTransport';
import { VideoExportPanel } from '../components/sync/VideoExportPanel';
import { useSceneEdit, type EditFn } from '../components/scene/useSceneEdit';
import { LayerInspector } from '../components/visualizer/LayerInspector';
import { Alert, Button, Panel, inputClass } from '../components/ui';
import * as av from '../domain/avOps';
import * as S from '../domain/sceneOps';
import * as viz from '../domain/visualizerOps';
import { MAX_VISUALIZER_LAYERS, VISUALIZER_TYPES, type AvSession, type Project, type VisualizerType } from '../domain/schemas';
import { debugCounters } from '../lib/debug';
import { useObservable, useObservableSelector } from '../lib/useObservable';
import { useVisualHistory } from '../state/historyContext';
import { useProject } from '../state/projectContext';
import { useServices } from '../state/services';
import { MAX_ANALYSIS_SECONDS } from '../visualizer/analysis';
import { needsAnalysis } from '../visualizer/evaluate';
import { VISUALIZER_DESCRIPTIONS, VISUALIZER_LABELS } from '../visualizer/layers';
import { VISUALIZER_PRESETS } from '../visualizer/presets';

/**
 * Music Visualizer Studio (Phase 4C). A visualizer is a set of layers on an audio-visual
 * session, so it plays on the Phase 4A session clock (music source, loop, seek, tempo),
 * draws in the session's scene through the scene renderer, and exports through the Phase
 * 4B video exporter. Layers driven by "measured" audio use a deterministic offline analysis
 * of the rendered music; "event-driven" layers use musical events and are labelled so.
 */
export function VisualizerWorkspace() {
  const { project } = useProject();
  const { sync, syncSelection, analysis } = useServices();
  const sessionId = useObservableSelector(sync.state, (s) => s.sessionId);
  const history = useVisualHistory();
  const historyState = useObservable(history.state);
  const { edit, error, setError } = useSceneEdit();
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('new');
  const session = project.avSessions.find((s) => s.id === sessionId) ?? null;
  // Leaving the workspace pauses session playback (it owns the shared audio engine).
  useEffect(() => () => sync.pause(), [sync]);
  useEffect(() => {
    debugCounters.visualizerAnalysis = () => {
      const s = analysis.state.get();
      return { status: s.status, seconds: s.seconds, renderMs: s.renderMs, analyzeMs: s.analyzeMs, computed: analysis.computed, started: analysis.started };
    };
    debugCounters.syncEvents = () => sync.events()?.events.map((e) => ({ id: e.id, category: e.category, tick: e.tick, seconds: e.seconds, velocity: e.velocity })) ?? null;
    return () => {
      debugCounters.visualizerAnalysis = undefined;
      debugCounters.syncEvents = () => null;
    };
  }, [analysis, sync]);

  const applyPreset = (presetId: string) => {
    let created = '';
    const ok = edit('Apply visualizer preset', (p) => {
      const options: viz.ApplyPresetOptions = target === 'replace' && session ? { sessionId: session.id } : target.startsWith('scene:') ? { sceneId: target.slice(6) } : {};
      const r = viz.applyPreset(p, presetId, options);
      created = r.sessionId;
      return r.project;
    });
    if (ok && created) syncSelection.selectSession(created);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="visualizer-workspace">
      <Panel
        title="Visualizer presets"
        actions={
          <>
            <Button size="sm" onClick={() => setHistoryError(history.undo())} disabled={!historyState.canUndo} title={historyState.undoLabel ? `Undo ${historyState.undoLabel}` : 'Undo'} data-testid="viz-undo">
              ↶ Undo
            </Button>
            <Button size="sm" onClick={() => setHistoryError(history.redo())} disabled={!historyState.canRedo} title={historyState.redoLabel ? `Redo ${historyState.redoLabel}` : 'Redo'} data-testid="viz-redo">
              ↷ Redo
            </Button>
          </>
        }
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Apply a preset to
            <select className={inputClass} value={target} onChange={(e) => setTarget(e.target.value)} data-testid="viz-preset-target">
              <option value="new">A new visualizer with its own backdrop</option>
              {project.scenes.map((s) => (
                <option key={s.id} value={`scene:${s.id}`}>
                  A new visualizer over the scene “{s.name}”
                </option>
              ))}
              {session && <option value="replace">The layers of “{session.name}” (replace)</option>}
            </select>
          </label>
          <p className="text-xs text-zinc-400">Presets are templates: applying one copies its layers; the built-in preset never changes.</p>
        </div>
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Visualizer presets" data-testid="viz-presets">
          {VISUALIZER_PRESETS.map((preset) => (
            <li key={preset.id} className="flex flex-col gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
              <div
                className="h-16 rounded-md"
                aria-hidden="true"
                style={{
                  background: `linear-gradient(135deg, ${preset.backdrop.background}, ${preset.backdrop.gradient?.to ?? preset.backdrop.background})`,
                  boxShadow: `inset 0 -20px 30px -10px ${preset.layers({ width: 1280, height: 720 })[0]?.style.primary ?? '#fff'}`,
                }}
              />
              <div className="text-sm font-semibold text-zinc-100">{preset.name}</div>
              <p className="flex-1 text-xs text-zinc-400">{preset.description}</p>
              <p className="text-[11px] text-zinc-400">{preset.layers({ width: 1280, height: 720 }).map((l) => VISUALIZER_LABELS[l.type]).join(' · ')}</p>
              <Button size="sm" variant="primary" onClick={() => applyPreset(preset.id)} data-testid={`viz-preset-${preset.id}`} aria-label={`Apply the ${preset.name} preset`}>
                Apply
              </Button>
            </li>
          ))}
        </ul>
        {(error || historyError) && (
          <div className="mt-3">
            <Alert title={error ?? historyError ?? ''} action={<Button size="sm" variant="ghost" onClick={() => (setError(null), setHistoryError(null))}>Dismiss</Button>} />
          </div>
        )}
      </Panel>
      {project.avSessions.length === 0 ? (
        <Panel title="Visualizer">
          <p className="text-sm text-zinc-400" data-testid="viz-empty">
            Apply a preset above to create a visualizer. It plays this project’s music (song or a looped pattern) and can sit behind or in front of a scene with characters and animation.
          </p>
        </Panel>
      ) : (
        session && <Studio project={project} session={session} edit={edit} />
      )}
    </div>
  );
}

function Studio({ project, session, edit }: { project: Project; session: AvSession; edit: EditFn }) {
  const { sync, syncSelection, analysis } = useServices();
  const durationSec = useObservableSelector(sync.clock, (c) => c.timing.durationSec);
  const analysisState = useObservable(analysis.state);
  const [selected, setSelected] = useState<string | null>(null);
  const [addType, setAddType] = useState<VisualizerType>('bars');
  const layers = session.visualizer.layers;
  const layer = layers.find((l) => l.id === selected) ?? layers[layers.length - 1] ?? null;
  const events = sync.events();
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const wantsAnalysis = needsAnalysis(session);
  const features = analysis.peek(project, session);
  const unavailable = analysis.unavailableReason(project, session);
  const key = analysis.keyFor(project, session);
  const [retries, setRetries] = useState(0);
  const latest = useRef({ project, session });
  useLayoutEffect(() => {
    latest.current = { project, session };
  });

  // Measured layers need the analysis: compute it once per music version (cached and shared
  // with export). The request follows the music key, not every edit, and is withdrawn when the
  // music changes or the workspace closes; the store cancels the computation only if no other
  // consumer (an export) still waits for it.
  useEffect(() => {
    if (!wantsAnalysis) return;
    const controller = new AbortController();
    const { project: p, session: s } = latest.current;
    analysis.ensure(p, s, controller.signal, { retry: retries > 0 }).catch(() => undefined);
    return () => controller.abort(new DOMException('The visualizer no longer needs this analysis', 'AbortError'));
  }, [analysis, key, wantsAnalysis, retries]);

  if (!events) return null;
  const flashing = layers.some((l) => l.visible && l.type === 'pulse' && l.params.flash > 0);
  const status = !wantsAnalysis ? 'not-needed' : features ? 'ready' : unavailable ? 'unavailable' : analysisState.key === key && analysisState.status === 'running' ? 'running' : 'pending';
  const music = session.music;
  const set = (label: string, patch: av.SessionPatch) => edit(label, (p) => av.updateSession(p, session.id, patch));

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <section aria-labelledby="viz-preview-heading" className="flex flex-col gap-2 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="viz-preview-heading" className="text-sm font-semibold tracking-wide text-zinc-200 uppercase">
              Preview · {session.name}
            </h2>
            <div className="flex flex-wrap gap-2 text-xs">
              <span
                role="status"
                aria-live="polite"
                className={`rounded px-2 py-0.5 ${status === 'ready' ? 'bg-emerald-900/60 text-emerald-200' : status === 'not-needed' ? 'bg-zinc-800 text-zinc-300' : 'bg-amber-900/50 text-amber-200'}`}
                data-testid="viz-analysis"
                data-status={status}
              >
                {status === 'ready' && `Measured audio analysis ready (${features!.durationSec.toFixed(1)} s analysed in ${((features!.renderMs + features!.analyzeMs) / 1000).toFixed(1)} s)`}
                {status === 'running' && `Analysing audio… ${Math.round(analysisState.progress * 100)}% — showing event-driven fallback meanwhile`}
                {status === 'pending' && 'Measured analysis pending — event-driven fallback'}
                {status === 'unavailable' && `Event-driven fallback: ${unavailable}`}
                {status === 'not-needed' && 'All layers are event-driven (no audio analysis needed)'}
              </span>
              {status === 'unavailable' && analysis.canRetry(project, session) && (
                <Button variant="secondary" onClick={() => setRetries((n) => n + 1)} data-testid="viz-analysis-retry">
                  Retry analysis
                </Button>
              )}
              {flashing && (
                <span className="rounded bg-red-950 px-2 py-0.5 text-red-200" data-testid="viz-flash-warning">
                  ⚠ Contains flashing
                </span>
              )}
            </div>
          </div>
          <SyncPreview project={project} session={session} />
          <SyncTransport project={project} session={session} sync={sync} edit={edit} />
        </section>
        <section aria-labelledby="viz-layers-heading" className="flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-3">
          <h2 id="viz-layers-heading" className="text-sm font-semibold tracking-wide text-zinc-200 uppercase">
            Layers ({layers.length}/{MAX_VISUALIZER_LAYERS})
          </h2>
          <div className="flex gap-2">
            <label className="sr-only" htmlFor="viz-add-type">
              Layer type to add
            </label>
            <select id="viz-add-type" className={`${inputClass} min-w-0 flex-1`} value={addType} onChange={(e) => setAddType(e.target.value as VisualizerType)} data-testid="viz-add-type">
              {VISUALIZER_TYPES.map((t) => (
                <option key={t} value={t} title={VISUALIZER_DESCRIPTIONS[t]}>
                  {VISUALIZER_LABELS[t]}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              variant="primary"
              disabled={layers.length >= MAX_VISUALIZER_LAYERS}
              onClick={() => {
                let id = '';
                if (
                  edit(`Add ${VISUALIZER_LABELS[addType]}`, (p) => {
                    const r = viz.addLayer(p, session.id, addType);
                    id = r.id;
                    return r.project;
                  })
                )
                  setSelected(id);
              }}
              data-testid="viz-add"
            >
              + Add
            </Button>
          </div>
          <p className="text-[11px] text-zinc-400">Top of the list draws in front. “Behind” layers sit between the scene background and its objects; “front” layers above the objects.</p>
          <ol className="flex flex-col-reverse gap-1" aria-label="Visualizer layers" data-testid="viz-layers">
            {layers.map((l, i) => {
              const fallbackL = l.response.source === 'measured' && !features;
              return (
                <li key={l.id} data-testid="viz-layer" data-id={l.id} data-type={l.type} className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm ${l.id === layer?.id ? 'bg-violet-950/60 ring-1 ring-violet-700' : 'bg-zinc-800/50'}`}>
                  <button type="button" className="min-w-0 flex-1 truncate text-left text-zinc-100" onClick={() => setSelected(l.id)} aria-pressed={l.id === layer?.id} data-testid="viz-layer-select">
                    {l.name}
                    <span className="ml-1 text-[11px] text-zinc-400">
                      {VISUALIZER_LABELS[l.type]} · {l.placement} · {l.response.source === 'measured' ? (fallbackL ? 'event fallback' : 'measured') : 'event-driven'}
                    </span>
                  </button>
                  <Button size="sm" variant="ghost" aria-label={`${l.visible ? 'Hide' : 'Show'} ${l.name}`} aria-pressed={!l.visible} onClick={() => edit(l.visible ? 'Hide layer' : 'Show layer', (p) => viz.setLayerVisible(p, session.id, l.id, !l.visible))} data-testid="viz-layer-visible">
                    {l.visible ? '👁' : '—'}
                  </Button>
                  <Button size="sm" variant="ghost" aria-label={`Move ${l.name} up`} disabled={i === layers.length - 1} onClick={() => edit('Move layer', (p) => viz.moveLayer(p, session.id, l.id, 1))} data-testid="viz-layer-up">
                    ↑
                  </Button>
                  <Button size="sm" variant="ghost" aria-label={`Move ${l.name} down`} disabled={i === 0} onClick={() => edit('Move layer', (p) => viz.moveLayer(p, session.id, l.id, -1))} data-testid="viz-layer-down">
                    ↓
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Duplicate ${l.name}`}
                    disabled={layers.length >= MAX_VISUALIZER_LAYERS}
                    onClick={() => {
                      let id = '';
                      if (
                        edit('Duplicate layer', (p) => {
                          const r = viz.duplicateLayer(p, session.id, l.id);
                          id = r.id;
                          return r.project;
                        })
                      )
                        setSelected(id);
                    }}
                    data-testid="viz-layer-duplicate"
                  >
                    ⧉
                  </Button>
                  <Button size="sm" variant="ghost" aria-label={`Delete ${l.name}`} onClick={() => edit('Delete layer', (p) => viz.deleteLayer(p, session.id, l.id))} data-testid="viz-layer-delete">
                    ✕
                  </Button>
                </li>
              );
            })}
          </ol>
          {layer ? <LayerInspector session={session} layer={layer} edit={edit} fallback={layer.response.source === 'measured' && !features} /> : <p className="text-xs text-zinc-400">No layers. Add one, or apply a preset.</p>}
        </section>
      </div>
      <Panel title="Composition">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Visualizer
            <select className={inputClass} value={session.id} onChange={(e) => syncSelection.selectSession(e.target.value)} data-testid="viz-session">
              {project.avSessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.visualizer.layers.length} layers)
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Music
            <select
              className={inputClass}
              value={music.kind === 'song' ? 'song' : music.patternId}
              onChange={(e) => set('Change music', { music: e.target.value === 'song' ? { kind: 'song' } : { kind: 'pattern', patternId: e.target.value, bars: music.kind === 'pattern' ? music.bars : 4 } })}
              data-testid="viz-music"
            >
              <option value="song">Song (arrangement)</option>
              {project.patterns.map((p) => (
                <option key={p.id} value={p.id}>
                  Pattern “{p.name}” (looped)
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Scene
            <select className={inputClass} value={session.sceneId} onChange={(e) => set('Change scene', { sceneId: e.target.value })} data-testid="viz-scene">
              {project.scenes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            Animation
            <select className={inputClass} value={session.animationId ?? ''} onChange={(e) => set('Change animation', { animationId: e.target.value || null })} data-testid="viz-animation">
              <option value="">None</option>
              {project.animations
                .filter((a) => a.sceneId === session.sceneId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex items-center justify-between gap-2 text-xs text-zinc-400">
            Background
            <input type="color" value={scene.background} onChange={(e) => edit('Change background', (p) => S.setSceneBackground(p, scene.id, { background: e.target.value }), `bg:${scene.id}`)} className="h-8 w-14 cursor-pointer rounded border border-zinc-700 bg-zinc-900" data-testid="viz-background" />
          </label>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={session.endBehavior === 'loop'} onChange={(e) => set('Change end behaviour', { endBehavior: e.target.checked ? 'loop' : 'stop' })} data-testid="viz-loop" />
            Loop the whole visualizer
          </label>
        </div>
        <p className="mt-2 text-[11px] text-zinc-400">Measured analysis covers up to {MAX_ANALYSIS_SECONDS / 60} minutes of music; longer sessions use the event-driven fallback.</p>
      </Panel>
      <Panel title="Timeline">
        <SyncTimeline project={project} session={session} events={events} durationSec={durationSec} sync={sync} selection={syncSelection} edit={edit} />
      </Panel>
      <Panel title="Video export">
        <VideoExportPanel project={project} session={session} />
      </Panel>
    </>
  );
}
